/**
 * Bridge between {@link AuthProvider}'s React state and the imperative
 * `getAccessToken()` callback that the API client (`@cguard/api-client`)
 * needs to attach `Authorization: Bearer <jwt>` to every fetch.
 *
 * Why a module-scoped ref instead of `useEffect` + closure?
 *   - `cguardFetcher` is configured once at app boot (see each app's
 *     `main.tsx`) and is called from outside React (e.g. SSE reconnects,
 *     orval-generated code that does not capture component state).
 *   - The token has to be read at request-time, not at hook-mount time —
 *     otherwise a refreshed token never makes it onto the wire.
 *   - This matches the pattern documented in the @cguard/api-client README:
 *     `getAccessToken: () => keycloak.token ?? null`.
 *
 * Usage in main.tsx (called once before `createRoot`):
 *
 *   ```ts
 *   configureCguardClient({
 *     baseUrl: '',
 *     getAccessToken: getCguardAccessToken,
 *   });
 *   ```
 *
 * The {@link AuthProvider} pumps the latest session into this module via
 * {@link bindAuthSessionToTokenSource} as soon as Keycloak hands it back.
 */

import type { AuthSession } from './types';

let currentSession: AuthSession | null = null;

/**
 * Set the active session. Called from {@link AuthProvider} when login
 * completes, when a silent renew swaps the access token, and when the
 * user signs out (passing `null`).
 *
 * Idempotent: passing the same session twice is a no-op.
 */
export function setActiveSession(session: AuthSession | null): void {
  currentSession = session;
}

/**
 * Read the active session synchronously. Returns `null` before login
 * completes or after sign-out.
 */
export function getActiveSession(): AuthSession | null {
  return currentSession;
}

/**
 * Returns the current bearer token, or `null` if the user is not yet
 * authenticated. Fetcher implementations should treat `null` as "do not
 * send an Authorization header" (anonymous request).
 *
 * Stable across renders — safe to pass directly to
 * `configureCguardClient({ getAccessToken: getCguardAccessToken })`.
 */
export function getCguardAccessToken(): string | null {
  return currentSession?.accessToken ?? null;
}

/**
 * Test helper: drop any session that survived from a previous test.
 * Production code should never call this.
 */
export function __resetTokenSourceForTests(): void {
  currentSession = null;
}
